import {
  contractTypeLabel,
  EMPLOYEE_FUNCTION_LABELS,
  YARD_STATE_LABELS,
  type YardAccessDenial,
} from "@etudescarre/constants";
import { Badge, Button, cn, LoadingSpinner, Modal } from "@etudescarre/ui";
import { isNil, isNotNil, type YardStatus } from "@etudescarre/utils";
import {
  ArrowRightIcon,
  BuildingsIcon,
  DownloadSimpleIcon,
  HardHatIcon,
  LockIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { DeactivatedLabel } from "@/features/admin/contracts-panel";
import {
  downloadNomenclature,
  downloadsRefusal,
} from "@/features/yard/download-nomenclature";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { YARD_ACCESS_DENIED_TITLE, yardAccessMessage } from "@/lib/yard-access";

type ContractItem = {
  employeeContractId: number;
  function: string | null;
  correspondenceMail: string | null;
  contractId: number;
  contractType: string;
  contractCode: string | null;
  isActive: boolean;
  yardId: number;
  yardName: string;
  yardType: string;
  comissioningDate: string | null;
  expirationDate: string | null;
  beforeArchivingDays: number | null;
  isLocked: boolean;
  yardStatus: YardStatus;
  isOff: boolean;
  moaCompanyName: string | null;
  performerCompanyName: string;
  referent: { name: string; email: string | null; isActive: boolean } | null;
  teams: Array<{
    id: number;
    name: string;
    members: Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      isReferent: boolean;
    }>;
  }>;
  accessDenial: YardAccessDenial | null;
  closedOn: string | null;
};

type GroupedYard = {
  yardId: number;
  yardName: string;
  yardType: string;
  comissioningDate: string | null;
  expirationDate: string | null;
  beforeArchivingDays: number | null;
  yardStatus: YardStatus;
  isOff: boolean;
  moaCompanyName: string | null;
  contracts: ContractItem[];
};

const YARD_TYPE_LABELS: Record<string, string> = {
  lots_separes: "Lots séparés",
  entreprise_generale: "Entreprise générale",
  conception_realisation: "Conception-réalisation",
};

const FUNCTION_LABELS = EMPLOYEE_FUNCTION_LABELS;

const STATUS_CONFIG: Record<
  YardStatus,
  { label: string; variant: "green" | "blue" | "yellow" | "red" }
> = {
  preparation: { label: YARD_STATE_LABELS.preparation, variant: "blue" },
  en_service: { label: YARD_STATE_LABELS.en_service, variant: "green" },
  archive: { label: YARD_STATE_LABELS.archive, variant: "yellow" },
  cloture: { label: YARD_STATE_LABELS.cloture, variant: "red" },
};

// Badges take a fixed width, sized on the longest label of their enum, so the
// same badge starts at the same place on every line, whatever its text.
const BADGE_CELL_CLASS = "shrink-0 justify-center whitespace-nowrap";

// Same column as the application header above it.
const PAGE_WIDTH_CLASS = "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8";

function groupByYard(contracts: ContractItem[]): GroupedYard[] {
  const map = new Map<number, GroupedYard>();

  for (const c of contracts) {
    const existing = map.get(c.yardId);
    if (existing) {
      existing.contracts.push(c);
    } else {
      map.set(c.yardId, {
        yardId: c.yardId,
        yardName: c.yardName,
        yardType: c.yardType,
        comissioningDate: c.comissioningDate,
        expirationDate: c.expirationDate,
        beforeArchivingDays: c.beforeArchivingDays,
        yardStatus: c.yardStatus,
        isOff: c.isOff,
        moaCompanyName: c.moaCompanyName,
        contracts: [c],
      });
    }
  }

  return Array.from(map.values());
}

function yardDateLines({
  comissioningDate,
  expirationDate,
  beforeArchivingDays,
}: GroupedYard): string[] {
  return [
    isNotNil(comissioningDate)
      ? `Mise en service ${formatDay(comissioningDate)}`
      : "Pas encore mis en service",
    isNotNil(expirationDate)
      ? `Expire ${formatDay(expirationDate)}`
      : "Date d'expiration non renseignée",
    isNotNil(beforeArchivingDays)
      ? `Durée archivage après expiration : ${beforeArchivingDays} ${beforeArchivingDays > 1 ? "jours" : "jour"}`
      : "Durée d'archivage non renseignée",
  ];
}

function YardStatusBadge({ group }: { group: GroupedYard }) {
  const [open, setOpen] = useState(false);
  const { yardName, yardStatus: status, isOff } = group;
  const config = STATUS_CONFIG[status];

  return (
    <span className="flex items-center gap-2">
      {/* flex: an inline badge would sit on the button's text baseline, lower
          than the badge beside it. */}
      <button
        type="button"
        aria-haspopup="dialog"
        className="flex w-28 cursor-pointer hover:opacity-80"
        onClick={() => setOpen(true)}
        title="Voir les dates du chantier"
      >
        <Badge
          variant={config.variant}
          className={cn("w-full", BADGE_CELL_CLASS)}
        >
          {config.label}
        </Badge>
      </button>
      {/* Kept empty when ON, or the badges of that line would shift. */}
      <span className="flex w-14">
        {isOff && (status === "en_service" || status === "archive") && (
          <Badge variant="red" className={cn("w-full", BADGE_CELL_CLASS)}>
            <LockIcon className="mr-0.5 inline size-3" />
            OFF
          </Badge>
        )}
      </span>
      <Modal.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>État du chantier - {yardName}</Modal.Title>
              </Modal.Header>
              <Modal.Body className="space-y-2 text-sm text-zinc-900 dark:text-zinc-100">
                {yardDateLines(group).map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => setOpen(false)}>Fermer</Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </span>
  );
}

function OrganisationModal({ contract }: { contract: ContractItem }) {
  const [open, setOpen] = useState(false);

  return (
    <Modal.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Button outline onClick={() => setOpen(true)} className="text-xs">
        <UsersIcon className="size-4" />
        Organisation
      </Button>
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="lg">
            <Modal.Header>
              <Modal.Title>Organisation - {contract.yardName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                    Contrat
                  </p>
                  <p className="text-zinc-900 dark:text-zinc-100">
                    {contractTypeLabel(contract.contractType)} -{" "}
                    {contract.performerCompanyName}
                    {contract.contractCode && ` (${contract.contractCode})`}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                    Référent contractuel
                  </p>
                  {isNil(contract.referent) ? (
                    <p className="text-zinc-400">Aucun</p>
                  ) : (
                    <p className="flex items-center gap-2 text-zinc-900 dark:text-zinc-100">
                      {contract.referent.name || contract.referent.email || "—"}
                      {contract.referent.name && contract.referent.email && (
                        <span className="text-sm text-zinc-500">
                          ({contract.referent.email})
                        </span>
                      )}
                      {!contract.referent.isActive && <DeactivatedLabel />}
                    </p>
                  )}
                </div>

                {contract.teams.length > 0 && (
                  <div>
                    <p className="mb-2 text-sm font-medium text-zinc-500 dark:text-zinc-400">
                      Équipes
                    </p>
                    <div className="space-y-3">
                      {contract.teams.map((team) => (
                        <div
                          key={team.id}
                          className="rounded-md border border-zinc-200 p-3 dark:border-zinc-700"
                        >
                          <p className="mb-1 font-medium text-zinc-900 dark:text-zinc-100">
                            {team.name}
                          </p>
                          {team.members.length > 0 ? (
                            <ul className="space-y-0.5 text-sm text-zinc-600 dark:text-zinc-400">
                              {team.members.map((m) => (
                                <li key={m.id}>
                                  {[m.firstName, m.lastName]
                                    .filter(Boolean)
                                    .join(" ") || "—"}
                                  {m.isReferent && (
                                    <Badge
                                      variant="purple"
                                      className="ml-2"
                                      size="sm"
                                    >
                                      Référent
                                    </Badge>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-zinc-400 italic">
                              Aucun membre
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setOpen(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}

// Why the yard does not open, null when it does. The refusal is notified on the
// selection page itself, never on a page of its own.
function accessRefusal(contract: ContractItem): string | null {
  return isNil(contract.accessDenial)
    ? null
    : yardAccessMessage(contract.accessDenial, contract.closedOn);
}

function ContractRow({ contract }: { contract: ContractItem }) {
  const navigate = useNavigate();
  const refusal = accessRefusal(contract);
  const isAccessible = isNil(refusal);
  const downloadRefusal = refusal ?? downloadsRefusal([contract.contractType]);
  const [isDownloading, setIsDownloading] = useState(false);
  const functionLabel = contract.function
    ? (FUNCTION_LABELS[contract.function] ?? contract.function)
    : null;

  const handleAccess = () => {
    if (isNil(refusal)) {
      navigate(`/yards/${contract.yardId}`);
      return;
    }
    toaster.error({ title: YARD_ACCESS_DENIED_TITLE, description: refusal });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadNomenclature(String(contract.yardId));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
        <Badge variant="blue" className={cn("w-32", BADGE_CELL_CLASS)}>
          {contractTypeLabel(contract.contractType)}
        </Badge>
        {/* Fixed columns: every contract lines up with the ones below it. */}
        <span
          className="w-52 shrink-0 truncate text-sm font-medium text-zinc-900 dark:text-zinc-100"
          title={contract.performerCompanyName}
        >
          {contract.performerCompanyName}
        </span>
        <span
          className="w-40 shrink-0 truncate text-sm text-zinc-500 dark:text-zinc-400"
          title={functionLabel ?? undefined}
        >
          {functionLabel}
        </span>
        {contract.correspondenceMail && (
          <span
            className="truncate text-xs text-zinc-400 dark:text-zinc-500"
            title={contract.correspondenceMail}
          >
            {contract.correspondenceMail}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <OrganisationModal contract={contract} />

        <Button
          outline
          disabled={isNotNil(downloadRefusal) || isDownloading}
          onClick={handleDownload}
          title={downloadRefusal ?? "Télécharger la nomenclature"}
          className="text-xs"
        >
          <DownloadSimpleIcon className="size-4" />
          Téléchargements
        </Button>

        {isAccessible ? (
          <Button onClick={handleAccess} className="text-xs">
            <ArrowRightIcon className="size-4" />
            Accéder
          </Button>
        ) : (
          <Button outline onClick={handleAccess} className="text-xs">
            <LockIcon className="size-4" />
            Accéder
          </Button>
        )}
      </div>
    </div>
  );
}

function YardGroup({ group }: { group: GroupedYard }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <BuildingsIcon className="size-5 shrink-0 text-zinc-600 dark:text-zinc-400" />
          <h2
            className="truncate text-lg font-semibold text-zinc-900 dark:text-zinc-100"
            title={group.yardName}
          >
            {group.yardName}
          </h2>
          {group.moaCompanyName && (
            <span
              className="truncate text-sm text-zinc-500 dark:text-zinc-400"
              title={group.moaCompanyName}
            >
              — {group.moaCompanyName}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="default" className={cn("w-36", BADGE_CELL_CLASS)}>
            {YARD_TYPE_LABELS[group.yardType] ?? group.yardType}
          </Badge>
          <YardStatusBadge group={group} />
        </div>
      </div>

      <div className="space-y-2">
        {group.contracts.map((c) => (
          <ContractRow key={c.employeeContractId} contract={c} />
        ))}
      </div>
    </div>
  );
}

export function OrganizationSelect() {
  const [contracts, setContracts] = useState<ContractItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await apiClient.GET("/api/my-contracts");

    if (apiError) {
      setError("Impossible de charger vos chantiers. Veuillez réessayer.");
    } else {
      setContracts(data?.data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const groups = groupByYard(contracts);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-zinc-50 dark:bg-zinc-950">
      <header className="shrink-0 border-b border-zinc-200 bg-white py-4 dark:border-zinc-800 dark:bg-zinc-900">
        <div className={cn("flex items-center gap-3", PAGE_WIDTH_CLASS)}>
          <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900/30">
            <HardHatIcon className="size-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
            Sélection de chantier
          </h1>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto overscroll-none">
        <div className={cn("py-8", PAGE_WIDTH_CLASS)}>
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <LoadingSpinner size="lg" className="text-blue-600" />
              <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
                Chargement de vos chantiers...
              </p>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="mb-4 text-red-600 dark:text-red-400">{error}</p>
              <Button onClick={fetchContracts}>Réessayer</Button>
            </div>
          )}

          {!loading && !error && groups.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 rounded-full bg-zinc-100 p-6 dark:bg-zinc-800">
                <BuildingsIcon className="size-12 text-zinc-400" />
              </div>
              <h2 className="mb-2 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Vous n'avez aucun chantier actif
              </h2>
              <p className="max-w-md text-sm text-zinc-500 dark:text-zinc-400">
                Vous n'êtes rattaché à aucun contrat actif pour le moment.
                Contactez votre administrateur si vous pensez qu'il s'agit d'une
                erreur.
              </p>
            </div>
          )}

          {!loading && !error && groups.length > 0 && (
            <div className="space-y-4">
              {groups.map((g) => (
                <YardGroup key={g.yardId} group={g} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
